import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import SuccessMessage from '../components/successMessage';
import LabOrderFormModal from './labOrderFormModal';
import LabOrderFilters from './labOrderFilters';
import LabOrderActionsMenu from './labOrderActionsMenu';
import LabOrderStatusChip from './labOrderStatusChip';
import { useLabOrdersScreen } from './hooks/useLabOrdersScreen';
import { formatCurrency, formatDisplayDate, isLabOrderLate } from './labOrderStatus';

const describeWork = (labOrder) =>
  [labOrder.tooth && `Dente ${labOrder.tooth}`, labOrder.shade && `Cor ${labOrder.shade}`]
    .filter(Boolean)
    .join(' · ');

export default function LabOrdersWeb() {
  const screen = useLabOrdersScreen();
  const {
    labOrders,
    lateCount,
    toSendCount,
    status,
    setStatus,
    totalAmount,
    isLoading,
    onlyLate,
    setOnlyLate,
    openMenu,
    openFormModal,
    labOrderToEdit,
    addLabOrder,
    closeFormModal,
    saveSuccess,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = screen;

  return (
    <Box sx={{ p: 3 }}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Laboratório
          </Typography>
          {lateCount > 0 && !onlyLate && (
            <Chip
              color="error"
              label={`${lateCount} atrasado${lateCount > 1 ? 's' : ''}`}
              onClick={() => setOnlyLate(true)}
            />
          )}
          {toSendCount > 0 && status !== 'TO_SEND' && (
            <Chip
              color="secondary"
              variant="outlined"
              label={`${toSendCount} a enviar`}
              onClick={() => setStatus('TO_SEND')}
            />
          )}
        </Stack>
        <Button variant="outlined" onClick={addLabOrder} data-cy="btn-add-lab-order">
          Novo Envio
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
        <LabOrderFilters screen={screen} />
      </Paper>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Paciente</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Procedimento</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Laboratório</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Envio</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Previsão</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Retorno</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Valor
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {labOrders.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {onlyLate
                      ? 'Nenhum envio atrasado.'
                      : 'Nenhum envio ao laboratório encontrado para os filtros.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {labOrders.map((labOrder) => (
              <TableRow key={labOrder.id} hover>
                <TableCell>{labOrder.patientName}</TableCell>
                <TableCell>
                  <Typography variant="body2">{labOrder.procedure}</Typography>
                  {describeWork(labOrder) && (
                    <Typography variant="caption" color="text.secondary">
                      {describeWork(labOrder)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{labOrder.laboratoryName}</TableCell>
                <TableCell>{labOrder.dentistName ?? '-'}</TableCell>
                <TableCell>{formatDisplayDate(labOrder.sentDate)}</TableCell>
                <TableCell
                  sx={{
                    color: isLabOrderLate(labOrder) ? 'error.main' : 'inherit',
                    fontWeight: isLabOrderLate(labOrder) ? 'bold' : 'normal',
                  }}
                >
                  {formatDisplayDate(labOrder.expectedReturnDate)}
                </TableCell>
                <TableCell>{formatDisplayDate(labOrder.returnDate)}</TableCell>
                <TableCell align="right">
                  {labOrder.amount != null ? formatCurrency(labOrder.amount) : '-'}
                </TableCell>
                <TableCell>
                  <LabOrderStatusChip labOrder={labOrder} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={(event) => openMenu(event, labOrder)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {labOrders.length > 0 && (
        <Stack direction="row" justifyContent="flex-end" spacing={3} sx={{ mt: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Envios: <strong>{labOrders.length}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total (sem cancelados): <strong>{formatCurrency(totalAmount)}</strong>
          </Typography>
        </Stack>
      )}

      <LabOrderActionsMenu screen={screen} />

      <LabOrderFormModal
        open={openFormModal}
        onClose={closeFormModal}
        onSaveSuccess={saveSuccess}
        labOrder={labOrderToEdit}
      />
    </Box>
  );
}
